import { Currency } from '@hero/schema';
import { CONFIG } from '../config';
import { roundNum } from '../utils';

/**
 * this function is very crucial when working with prices.
 *
 * It will round prices to 2 decimal points. Ex 100.98912 will be rounded to 100.99
 * @param price unrounded price or any price number
 * @returns rounded price
 */
export const roundPrice = (price: number): number => {
  const decimals = CONFIG.price.priceDecimals;
  const roundedPrice = roundNum(price, decimals);
  return roundedPrice;
};

export const formatPrice = (price: number, currency: Currency) => {
  const rounded = roundPrice(price);
  const formatted = `${rounded} ${currency}`;
  return formatted;
};

export const roundPriceToNearest50 = (price: number) => {
  if (price === 0) return 0;
  // Find the remainder when divided by 100
  const remainder = price % 100;
  let newPrice = price;
  // If the remainder is less than 50, subtract it from the number and add 50
  // Otherwise, subtract the remainder and add 100
  if (remainder < 50) {
    newPrice = newPrice - remainder + 50;
  } else {
    newPrice = newPrice - remainder + 100;
  }
  return Math.floor(newPrice);
};

/**
 * round any percentage as per the config
 * @param percentage a percentage such as 0.1232
 * @returns a rounded percentage suh as 0.12
 */
export const roundPercentage = (percentage: number) => {
  if (isFinite(percentage) === false) return 0;
  const decimals = CONFIG.price.percentageDecimals;
  const rounded = roundNum(percentage, decimals);
  return rounded;
};

/**
 * Format a number with comma separators (e.g., 1500000 -> "1,500,000")
 * Rounds the price first, then formats with commas.
 */
export const formatNumberWithCommas = (value: number): string => {
  const rounded = roundPrice(value);
  const parts = rounded.toString().split('.');
  const integerPart = parts[0];
  const decimalPart = parts[1];
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return decimalPart !== undefined ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

export const displayPrice = (price: number, currency: Currency, displayCompactNumber = false) => {
  const rounded = roundPrice(price);
  const isNegative = rounded < 0;
  const absolute = Math.abs(rounded);
  const formatted = displayCompactNumber
    ? formatCompactNumber(absolute)
    : absolute.toLocaleString('en-US');
  return isNegative ? `- ${currency} ${formatted}` : `${currency} ${formatted}`;
};
export const displayPriceWithOutTax = (price: number, currency: Currency) => {
  const formatted = displayPrice(price, currency);
  return `${formatted} غير شامل الضريبة`;
};

/**
 * Format a number in compact notation (e.g., 10.1K, 1.5M)
 * @param value The number to format
 * @returns Formatted string like "10.1K" or "1.5M"
 */
export const formatCompactNumber = (value: number): string => {
  if (value >= 1000000) {
    const millions = value / 1000000;
    // Show one decimal place if needed
    if (millions % 1 === 0) {
      return `${Math.floor(millions)}M`;
    }
    return `${millions.toFixed(1)}M`;
  } else if (value >= 1000) {
    const thousands = value / 1000;
    // Show one decimal place if needed
    if (thousands % 1 === 0) {
      return `${Math.floor(thousands)}K`;
    }
    return `${thousands.toFixed(1)}K`;
  }
  return value.toString();
};
